import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, ChevronRight, Phone, ArrowLeft } from 'lucide-react';
import { LOGO, PHONE, API } from '@/pages/LandingPage';

export default function OnlineRegister() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialPlan = params.get('plan') || 'mcq';
  const initialRef = params.get('ref') || '';
  const [plans, setPlans] = useState({});
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', plan: initialPlan, referral_code: initialRef });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Join Online LDC Batch | Signature PSC — From ₹199';
    axios.get(`${API}/online/plans`).then(r => setPlans(r.data.plans || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.password) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/online/register`, form);
      toast.success('Registered! 3-day free trial activated.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0c1a0e]" data-testid="online-register-page">
      <nav className="bg-[#0c1a0e]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="" className="w-9 h-9 rounded-full bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>SIGNATURE PSC</span>
              <span className="text-[10px] block text-[#c8b87a] tracking-[0.15em] uppercase -mt-0.5">Online LDC Batch</span>
            </div>
          </Link>
          <Link to="/" className="text-sm text-white/70 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-8">
        {/* Plans showcase */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 space-y-4">
          <div className="mb-2">
            <Sparkles className="w-7 h-7 text-[#c8a84e] mb-2" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Choose your Online LDC Plan</h1>
            <p className="text-white/60 text-sm">3-day FREE TRIAL · Then pay only ₹{plans[form.plan]?.price || 0}/month via Google Pay</p>
          </div>
          {Object.entries(plans).map(([key, p]) => (
            <button key={key} onClick={() => setForm({ ...form, plan: key })}
              className={`w-full text-left p-5 rounded-2xl border transition-all ${form.plan === key ? 'border-[#c8a84e] bg-[#c8a84e]/10 shadow-[0_8px_30px_rgba(200,168,78,0.2)]' : 'border-white/10 bg-[#142318] hover:border-white/30'}`}
              data-testid={`plan-card-${key}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-base font-bold text-white">{p.name}</p>
                  <p className="text-xs text-white/50 mt-0.5">{key === 'basic' ? 'Best for self-learners' : key === 'mcq' ? 'Most popular' : 'Complete preparation'}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#c8a84e]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{p.price}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">per month</p>
                </div>
              </div>
              <ul className="space-y-1.5">
                {p.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-white/70"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              {form.plan === key && <div className="mt-3 text-xs text-[#c8a84e] font-semibold">✓ Selected</div>}
            </button>
          ))}
        </motion.div>
        {/* Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-2">
          <div className="p-6 rounded-2xl bg-[#142318] border border-white/10 sticky top-6">
            <h2 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Create your account</h2>
            <p className="text-xs text-white/55 mb-5">Start your 3-day free trial · No payment now</p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Full Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-[#0c1a0e] border-white/10 text-white" required data-testid="reg-name" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">WhatsApp Number *</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-[#0c1a0e] border-white/10 text-white" required data-testid="reg-phone" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[#0c1a0e] border-white/10 text-white" required data-testid="reg-email" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Password * (min 6 chars)</Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-[#0c1a0e] border-white/10 text-white" required data-testid="reg-password" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Referral Code (optional)</Label>
                <Input value={form.referral_code} onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} className="bg-[#0c1a0e] border-white/10 text-white" placeholder="If a friend referred you" data-testid="reg-referral" />
              </div>
              <div className="p-3 rounded-lg bg-[#c8a84e]/10 border border-[#c8a84e]/20 text-xs text-white/70">
                Selected: <strong className="text-white">{plans[form.plan]?.name}</strong> · ₹{plans[form.plan]?.price}/month
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white font-semibold py-3 rounded-full" data-testid="reg-submit">
                {loading ? 'Creating...' : 'Start 3-Day Free Trial'} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <p className="text-[11px] text-white/45 text-center mt-2">Already registered? <Link to="/login" className="text-[#c8a84e] hover:underline">Login</Link></p>
              <a href={`tel:+91${PHONE}`} className="block text-center text-[11px] text-white/55 mt-1"><Phone className="w-3 h-3 inline mr-1" /> Need help? Call {PHONE}</a>
            </form>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
